import { useFlyOutProductsBasedOnSelectedCloud } from '../../../common/utils/hooks/useFlyOutProductsBasedOnSelectedCloud'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { clsx } from 'clsx'
import { isEmpty } from 'lodash'
import { useLocation, useParams } from 'react-router-dom'
import {
  ButtonV2,
  Filters,
  RadioButtonsV2,
  SectionProps,
  VerticalDivider,
  SearchBoxComponent,
} from '@labrav/react-components'
import {
  isLoading,
  LoadingTypes,
  updateAppAlert,
  useAccountId,
  useCurrentCloudProvider,
  useScreenSize,
  getLabelFromMapping,
} from '@labrav/common'

import { RevenueInsightsTableView } from '../RevenueInsightsTableView/RevenueInsightsTableView'
import { useStyles } from './RevenueInsightsTableWithFilters.styles'
import {
  DisbursedByDateRangeMapping,
  PaymentDelayedSinceMapping,
  PaymentDueDateMapping,
  offerTypeRadioMapping,
  paymentStatusKeys,
  paymentStatusRadioMapping,
} from '../../../common/utils/revenueInsightsFilterMapping'
import {
  DateRangesTypeStatus,
  FiltersState,
  ReportsRow,
} from '../../../modules/revenueInsightsV2/types'
import {
  clearRevenueFilters,
  clearRevenueReports,
  getRevenueInsights,
  setFilters,
} from '../../../modules/revenueInsightsV2/action'
import { useDispatch, useSelector } from '../../../store/hooks'
import {
  searchDropDownOptionMapping,
  searchDropDownOptions,
} from '../../../common/utils/meteringConstants'
import { SearchResultsCount } from '@labrav/common'

export const RevenueInsightsTableWithFilters: React.FC<{
  isDataFeedsConnected?: boolean
}> = ({ isDataFeedsConnected }) => {
  const dispatch = useDispatch()
  const { accountId: routeAccountId } = useParams<{
    cloud: string
    accountId: string
  }>()
  const fallbackAccountId = useAccountId({ product: 'flyout' }) || ''
  const accountId = routeAccountId || fallbackAccountId
  const {
    paymentStatus: paymentStatusByDefault,
    offerType: offerTypeByDefault,
    disbursementDate: disbursementDateByDefault,
    paymentDueDate: paymentDueDateByDefault,
    paymentDelayedSince: paymentDelayedSinceByDefault,
  } = useSelector<FiltersState>(state => state.revenueInsightsV2.filters)
  const tcv = useSelector<number | undefined>(
    state => state.revenueInsightsV2.tcv
  )
  const [searchNow, setSearchNow] = useState(false)
  const [showCrossMark, setShowCrossMark] = useState(false)
  const [inputValue, setInputValue] = useState('')
  const [selectedOption, setSelectedOption] = useState(
    searchDropDownOptions.revenueInsightsOptions[0]
  )
  const { classes } = useStyles({ searchNow })
  const pageNumber = useSelector<number | undefined>(
    state => state.revenueInsightsV2.pageNumber
  )
  const count = useSelector<number | undefined>(
    state => state.revenueInsightsV2.count
  )
  const { search } = useLocation()
  const loading = useSelector<boolean>(state =>
    isLoading(state.loading)(LoadingTypes.GENERAL)
  )
  const { products, selectedProduct: productId } =
    useFlyOutProductsBasedOnSelectedCloud()

  const reportsRows = useSelector<ReportsRow[]>(
    state => state.revenueInsightsV2.records
  )

  const [paymentStatusValue, setPaymentStatusValue] = useState<string>(
    paymentStatusByDefault
  )
  const [offerTypeValue, setOfferTypeValue] =
    useState<string>(offerTypeByDefault)
  const [disbursedByDateRange, setDisbursedByDateRange] = useState<string>(
    disbursementDateByDefault
  )
  const [paymentDueDate, setPaymentDueDate] = useState<string>(
    paymentDueDateByDefault
  )
  const [paymentDelayedSince, setPaymentDelayedSince] = useState<string>(
    paymentDelayedSinceByDefault
  )

  const { pageSize: pageSizeValue } = useScreenSize()
  const selectedCloudTypeProductListing = useCurrentCloudProvider()

  useEffect(() => {
    if (productId !== undefined && !isEmpty(products)) {
      dispatch(clearRevenueReports())
      dispatch(
        getRevenueInsights({
          downloadReport: false,
          partnerId: accountId,
          pageSizeValue,
          input: inputValue,
          selectedInput:
            searchDropDownOptionMapping.revenueInsightsOptions[
              selectedOption as keyof typeof searchDropDownOptionMapping.revenueInsightsOptions
            ],
          sectionName: 'revenue-feeds',
          pageNumber,
          cloudMarketplace: selectedCloudTypeProductListing,
        })
      )
    }
    return () => {
      dispatch(
        updateAppAlert({
          message: undefined,
          messageType: 'INFO',
          autoClose: true,
        })
      )

      dispatch(clearRevenueReports())
    }
  }, [productId, search, searchNow, products, selectedCloudTypeProductListing])

  const handlePaymentStatus = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPaymentStatusValue(e.target.value)
  }
  const handleOfferType = (e: React.ChangeEvent<HTMLInputElement>) => {
    setOfferTypeValue(e.target.value)
  }
  const handleDisbursementDateRange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setDisbursedByDateRange(e.target.value)
  }
  const handlePaymentDueDate = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPaymentDueDate(e.target.value)
  }
  const handlePaymentDelayed = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPaymentDelayedSince(e.target.value)
  }
  const disbursedByDateRangeLabel = useMemo(
    () =>
      getLabelFromMapping(DisbursedByDateRangeMapping, disbursedByDateRange),
    [disbursedByDateRange]
  )

  const paymentDueDateRangeLabel = useMemo(
    () => getLabelFromMapping(PaymentDueDateMapping, paymentDueDate),
    [paymentDueDate]
  )

  const paymentDelayedSinceRangeLabel = useMemo(
    () => getLabelFromMapping(PaymentDelayedSinceMapping, paymentDelayedSince),
    [paymentDelayedSince]
  )

  const paymentStatusLabel = useMemo(() => {
    return (
      Object.keys(paymentStatusKeys).find(
        key => paymentStatusKeys[key] === paymentStatusValue
      ) || paymentStatusValue
    )
  }, [paymentStatusValue])
  const sections: SectionProps[] = [
    {
      title: 'Payment status',
      type: 'radio',
      content: (
        <div data-testid="paymentStatus-radio" className={classes.radioButton}>
          <RadioButtonsV2
            checkedValue={{
              key: paymentStatusValue || '',
              value: paymentStatusValue || '',
            }}
            name="paymentStatus"
            radioButtonMappings={paymentStatusRadioMapping}
            handleChange={handlePaymentStatus}
            vertical
          />
        </div>
      ),
      selectedValues: paymentStatusLabel || undefined,
      onDelete: () => setPaymentStatusValue(DateRangesTypeStatus.ALL),
    },
    {
      title: 'Offer type',
      type: 'radio',
      content: (
        <div data-testid="offerTypeValue-radio">
          <RadioButtonsV2
            checkedValue={{
              key: offerTypeValue || '',
              value: offerTypeValue || '',
            }}
            name="offerType"
            radioButtonMappings={offerTypeRadioMapping}
            handleChange={handleOfferType}
            isFlexOne={false}
            gapBetween="40px"
          />
        </div>
      ),
      selectedValues: offerTypeValue || undefined,
      onDelete: () => setOfferTypeValue(DateRangesTypeStatus.ALL),
    },
    {
      title: 'Disbursed by date range',
      type: 'radio',
      content: (
        <div
          className={classes.radioButton}
          data-testid="disbursedByDateRange-radio"
        >
          <RadioButtonsV2
            checkedValue={{
              key: disbursedByDateRange || '',
              value: disbursedByDateRange || '',
            }}
            name="disbursedByDateRange"
            radioButtonMappings={DisbursedByDateRangeMapping}
            handleChange={handleDisbursementDateRange}
            vertical
          />
        </div>
      ),
      selectedValues: disbursedByDateRangeLabel || undefined,
      onDelete: () => setDisbursedByDateRange(DateRangesTypeStatus.ALL),
    },
    {
      title: 'Payment due (includes past dues)',
      type: 'radio',
      content: (
        <div className={classes.radioButton} data-testid="paymentDueDate-radio">
          <RadioButtonsV2
            checkedValue={{
              key: paymentDueDate || '',
              value: paymentDueDate || '',
            }}
            name="paymentDueDate"
            radioButtonMappings={PaymentDueDateMapping}
            handleChange={handlePaymentDueDate}
            vertical
          />
        </div>
      ),
      selectedValues: paymentDueDateRangeLabel || undefined,
      onDelete: () => setPaymentDueDate(DateRangesTypeStatus.ALL),
    },
    {
      title: 'Payment delayed since',
      type: 'radio',
      content: (
        <div
          className={classes.radioButton}
          data-testid="paymentDelayedSince-radio"
        >
          <RadioButtonsV2
            checkedValue={{
              key: paymentDelayedSince || '',
              value: paymentDelayedSince || '',
            }}
            name="paymentDelayedSince"
            radioButtonMappings={PaymentDelayedSinceMapping}
            handleChange={handlePaymentDelayed}
            vertical
          />
        </div>
      ),
      selectedValues: paymentDelayedSinceRangeLabel || undefined,
      onDelete: () => setPaymentDelayedSince(DateRangesTypeStatus.ALL),
    },
  ]

  const applyFilters = useCallback(async () => {
    const filters: FiltersState = {
      paymentStatus: paymentStatusValue || 'All',
      offerType: offerTypeValue || 'All',
      disbursementDate: disbursedByDateRange || 'All',
      paymentDueDate: paymentDueDate || 'All',
      paymentDelayedSince: paymentDelayedSince || 'All',
    }
    await dispatch(setFilters(filters))
    await dispatch(clearRevenueReports())
    dispatch(
      getRevenueInsights({
        downloadReport: false,
        partnerId: accountId,
        pageSizeValue,
        cloudMarketplace: selectedCloudTypeProductListing,
      })
    )
  }, [
    paymentStatusValue,
    offerTypeValue,
    disbursedByDateRange,
    paymentDueDate,
    paymentDelayedSince,
  ])
  const handleButtonClick = async () => {
    await dispatch(
      getRevenueInsights({
        downloadReport: true,
        partnerId: accountId,
        pageSizeValue,
        input: inputValue,
        selectedInput:
          searchDropDownOptionMapping.revenueInsightsOptions[
            selectedOption as keyof typeof searchDropDownOptionMapping.revenueInsightsOptions
          ],
        sectionName: 'revenue-feeds',
        pageNumber,
        cloudMarketplace: selectedCloudTypeProductListing,
      })
    )
  }
  const handleSearchClick = async () => {
    if (inputValue.trim() !== '') {
      setSearchNow(true)
      setShowCrossMark(true)
      await dispatch(clearRevenueReports())
      await dispatch(clearRevenueFilters())
      await dispatch(
        getRevenueInsights({
          downloadReport: false,
          partnerId: accountId,
          pageSizeValue,
          input: inputValue,
          selectedInput:
            searchDropDownOptionMapping.revenueInsightsOptions[
              selectedOption as keyof typeof searchDropDownOptionMapping.revenueInsightsOptions
            ],
          sectionName: 'revenue-feeds',
          pageNumber,
          cloudMarketplace: selectedCloudTypeProductListing,
        })
      )
    }
  }
  return (
    <div className={classes.tabelWrapper}>
      <div className={clsx(classes.alignFilters, classes.justifySpaceBetween)}>
        {selectedCloudTypeProductListing === 'AWS' ? (
          <SearchBoxComponent
            options={searchDropDownOptions.revenueInsightsOptions}
            handleSearchClick={handleSearchClick}
            loading={loading}
            searchNow={searchNow}
            setSearchNow={setSearchNow}
            showCrossMark={showCrossMark}
            setShowCrossMark={setShowCrossMark}
            inputValue={inputValue}
            setInputValue={setInputValue}
            selectedOption={selectedOption}
            setSelectedOption={setSelectedOption}
          />
        ) : (
          <></>
        )}
        {selectedCloudTypeProductListing === 'AWS' ? (
          <div className={classes.filterWrapper}>
            {' '}
            <ButtonV2
              onClick={handleButtonClick}
              data-testid="download-csv-file"
              disabled={!isDataFeedsConnected}
            >
              Download CSV
            </ButtonV2>
            <VerticalDivider />
            <div className={classes.filter}>
              <Filters
                sections={sections}
                applyFilters={applyFilters}
                minWidth="575px"
              />
            </div>
          </div>
        ) : (
          <></>
        )}
      </div>
      {searchNow && selectedCloudTypeProductListing === 'AWS' ? (
        <>
          <SearchResultsCount totalCount={count as number} loading={loading} />
          {(count as number) > 0 && (
            <RevenueInsightsTableView
              isDataFeedsConnected={isDataFeedsConnected}
              reportsRows={reportsRows}
              input={inputValue}
              selectedOption={selectedOption}
            />
          )}
        </>
      ) : (
        <RevenueInsightsTableView
          isDataFeedsConnected={isDataFeedsConnected}
          reportsRows={reportsRows}
          input={inputValue}
          selectedOption={selectedOption}
        />
      )}
      {/* {isDataFeedsConnected && !searchNow && (
        <Typography
          className={classes.totalNetRevenue}
          data-testid="total-net-revenue"
        >
          Total of net revenue for selected period & filters:
          <span className={classes.totalPrice}>{` $${formatToDecimalPlaces(
            tcv
          )}`}</span>
        </Typography>
      )} */}
    </div>
  )
}
